import {ChangeDetectionStrategy, Component, Injectable, input, linkedSignal, output, signal} from '@angular/core';
import {MatButton} from '@angular/material/button';
import {TranslateModule} from '@ngx-translate/core';
import {Temporal} from 'temporal-polyfill';
import {AvtaledetaljerModell, AvtaleId, AvtaleModell, AvtaleRessursClient, Avtalestatus, GruppeAggreatModell, GruppeId, GrupperAggreatModell, GruppeRessursClient, PrøveperiodeUtløpsdatoSpesifikasjon, TerminerAvtaleResponsModell} from 'felles-ng2/tjenester/ressurser/generertNg2HttpKlient';
import {FormaterPlainDatePipe} from 'felles-ng2/pipes/FormaterPlainDatePipe';
import {ZonedDateTimeSomPlainDatePipe} from 'felles-ng2/pipes/ZonedDateTimeSomPlainDatePipe';
import {Dialog} from 'felles-ng2/tjenester/Dialog';
import {i18n, Oversettelser} from 'felles-ng2/tjenester/i18n/Oversettelser';
import {lesZonedDateTimeSerialisert, skrivZonedDateTimeSerialisert} from 'felles-ng2/støttefunksjoner/temporal';
import {HtmlFormatertKomponent} from 'web-ng2/delt/komponenter/html-formatert/html-formatert';
import {RedigerAvtaleKomponent} from 'web-ng2/moduler/avtale/komponenter/rediger-avtale/rediger-avtale';
import {AdministrasjonBrukerKomponent} from '../administrasjon-bruker/administrasjon-bruker';
import {AdministrasjonGruppeKomponent} from '../administrasjon-gruppe/administrasjon-gruppe';

@Component({
    selector: 'administrasjon-avtale',
    templateUrl: 'administrasjon-avtale.html',
    styleUrl: 'administrasjon-avtale.scss',
    imports: [AdministrasjonBrukerKomponent, AdministrasjonGruppeKomponent, FormaterPlainDatePipe, HtmlFormatertKomponent, MatButton, RedigerAvtaleKomponent, TranslateModule, ZonedDateTimeSomPlainDatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdministrasjonAvtaleKomponent {
    static ajsSelector = 'administrasjonAvtale';

    avtale = input.required<AvtaleModell>();
    readonly avtaleOppdatert = output<AvtaleModell>();
    readonly visningsavtale = linkedSignal(() => this.avtale());
    readonly viserDetaljer = signal(false);
    readonly viserFakturaOgAvtaleinformasjon = signal(false);
    readonly viserRedigerAvtale = signal(false);
    readonly redigererProeveperiode = signal(false);
    readonly proeveperiodeUtloepsdato = signal('');
    readonly detaljer = signal<AvtaledetaljerModell | null>(null);
    readonly gruppeaggregater = signal<Map<GruppeId, GruppeAggreatModell>>(new Map());

    constructor(private avhengigheter: AdministrasjonAvtaleAvhengigheter,
                private dialog: Dialog,
                private oversettelser: Oversettelser) {}

    async byttDetaljvisning(): Promise<void> {
        this.viserDetaljer.update(vises => !vises);
        if (this.viserDetaljer() && this.detaljer()?.id !== this.visningsavtale().id)
            await this.lastDetaljer();
    }

    byttRedigeringAvProeveperiode(): void {
        this.redigererProeveperiode.update(vises => !vises);
    }

    byttVisningAvFakturaOgAvtaleinformasjon(): void {
        this.viserFakturaOgAvtaleinformasjon.update(vises => !vises);
    }

    byttVisningAvRedigerAvtale(): void {
        this.viserRedigerAvtale.update(vises => !vises);
    }

    async lagreProeveperiode(): Promise<void> {
        const dato = Temporal.PlainDate.from(this.proeveperiodeUtloepsdato());
        const proeveperiodeUtloepsdato = skrivZonedDateTimeSerialisert(dato.toZonedDateTime({timeZone: 'UTC', plainTime: Temporal.PlainTime.from('00:00')}));
        const avtale = await this.avhengigheter.oppdaterProeveperiode(this.visningsavtale().id, {proeveperiodeUtloepsdato});
        this.oppdaterAvtale(avtale);
        this.redigererProeveperiode.set(false);
        await this.lastDetaljer();
    }

    async terminerAvtale(): Promise<void> {
        const avtale = this.visningsavtale();
        const bekreftet = await this.dialog.fare({
            tittel: this.oversettelser.oversett(i18n('Avslutt avtale')),
            innhold: this.oversettelser.oversett(i18n('Er du sikker på at du vil avslutte avtalen {avtale}?'), {avtale: avtale.navn}),
            okKnapp: this.oversettelser.oversett(i18n('Avslutt avtale'))
        });
        if (!bekreftet) return;

        const respons = await this.avhengigheter.terminerAvtale(avtale.id);
        this.oppdaterAvtale(respons.avtale);
        await this.lastDetaljer();
    }

    async reaktiverAvtale(): Promise<void> {
        const avtale = this.visningsavtale();
        const bekreftet = await this.dialog.info({
            tittel: this.oversettelser.oversett(i18n('Reaktiver avtale')),
            innhold: this.oversettelser.oversett(i18n('Vil du reaktivere avtalen {avtale}?'), {avtale: avtale.navn}),
            okKnapp: this.oversettelser.oversett(i18n('Reaktiver avtale'))
        });
        if (!bekreftet) return;

        this.oppdaterAvtale(await this.avhengigheter.reaktiverAvtale(avtale.id));
        await this.lastDetaljer();
    }

    async vedAvtaleRedigert(detaljer: AvtaledetaljerModell): Promise<void> {
        this.detaljer.set(detaljer);
        this.oppdaterAvtale(await this.avhengigheter.hentEnkel(detaljer.id));
    }

    erIProeveperiode(): boolean {
        return this.visningsavtale().avtalestatus === Avtalestatus.prøveperiode;
    }

    erProeveperiodeUtloept(): boolean {
        return this.visningsavtale().avtalestatus === Avtalestatus.prøveperiodeUtløpt;
    }

    erTerminert(): boolean {
        return this.visningsavtale().avtalestatus === Avtalestatus.terminert;
    }

    kanRedigereProeveperiode(): boolean {
        return this.erIProeveperiode() || this.erProeveperiodeUtloept();
    }

    kanTerminereAvtale(): boolean {
        return this.visningsavtale().avtalestatus === Avtalestatus.aktiv;
    }

    private async lastDetaljer(): Promise<void> {
        const id = this.visningsavtale().id;
        const [detaljer, aggregater] = await Promise.all([
            this.avhengigheter.hent(id),
            this.avhengigheter.gruppeaggregaterForAvtale(id)
        ]);
        if (id !== this.visningsavtale().id) return;

        this.detaljer.set(detaljer);
        this.proeveperiodeUtloepsdato.set(lesZonedDateTimeSerialisert(detaljer.proeveperiodeUtloepsdato).toPlainDate().toString());
        this.gruppeaggregater.set(new Map(aggregater.grupper.map(aggregat => [aggregat.gruppeId, aggregat])));
    }

    private oppdaterAvtale(avtale: AvtaleModell): void {
        this.visningsavtale.set(avtale);
        this.avtaleOppdatert.emit(avtale);
    }
}

@Injectable({providedIn: 'root'})
export class AdministrasjonAvtaleAvhengigheter {
    constructor(private avtaleRessurs: AvtaleRessursClient,
                private gruppeRessurs: GruppeRessursClient) {}

    hent(id: AvtaleId): Promise<AvtaledetaljerModell> {
        return this.avtaleRessurs.hent(id).somPromise();
    }

    hentEnkel(id: AvtaleId): Promise<AvtaleModell> {
        return this.avtaleRessurs.hentEnkel(id).somPromise();
    }

    gruppeaggregaterForAvtale(id: AvtaleId): Promise<GrupperAggreatModell> {
        return this.gruppeRessurs.gruppeAggregaterForAvtale(id).somPromise();
    }

    terminerAvtale(id: AvtaleId): Promise<TerminerAvtaleResponsModell> {
        return this.avtaleRessurs.adminTerminerAvtale(id).somPromise();
    }

    reaktiverAvtale(id: AvtaleId): Promise<AvtaleModell> {
        return this.avtaleRessurs.reaktiverAvtale(id).somPromise();
    }

    oppdaterProeveperiode(id: AvtaleId, spesifikasjon: PrøveperiodeUtløpsdatoSpesifikasjon): Promise<AvtaleModell> {
        return this.avtaleRessurs.oppdaterProeveperiodeUtloepsdato(id, spesifikasjon).somPromise();
    }
}
